<ng-container [formGroup]="formGroup">
    <mat-form-field appearance="outline"
                    class="w-100"
                    [class.bg-white]="!newStyles"
                    [class.payhoa-field]="newStyles">
        <mat-select [formControlName]="controlName"
                    [placeholder]="placeholder"
                    [compareWith]="comparator"
                    [multiple]="multiple">
            <div class="px-3 py-2">
                <lf-material-search (keydown)="$event.stopPropagation()" (updated)="$searchUpdated.next($event)"></lf-material-search>
            </div>

            <div *ngIf="hasRetiredAccounts" class="d-flex justify-content-between px-3 py-2">
                <label (click)="retiredToggle.toggleState()">
                    Show Retired
                </label>
                <lf-toggle-switch
                    #retiredToggle
                    (changed)="$showRetiredAccounts.next($event)"
                    [color]="'blue'"
                ></lf-toggle-switch>
            </div>

            <div *ngIf="hasDeletedAccounts" class="d-flex justify-content-between px-3 py-2">
                <label (click)="deletedToggle.toggleState()">
                    Show Deleted
                </label>
                <lf-toggle-switch
                    #deletedToggle
                    (changed)="$showDeletedAccounts.next($event)"
                    [color]="'blue'"
                ></lf-toggle-switch>
            </div>

            <mat-option *ngIf="allowUncategorized" [value]="null">{{ uncategorizedLabel }}</mat-option>

            <ng-container *ngIf="!isLoading">
                <ng-container *ngFor="let section of sections" [ngSwitch]="section">
                    <ng-container *ngSwitchCase="'revenue'">
                        <ng-container *ngIf="showRevenueCategories">
                            <ng-container *ngTemplateOutlet="categoryGroup; context:{group: {label: 'Revenue Categories', data$: $revenueCategories, legacyData$: $legacyDisabledAssets, category: 'income'}}"></ng-container>
                        </ng-container>
                    </ng-container>

                    <ng-container *ngSwitchCase="'expense'">
                        <ng-container *ngIf="showExpenseCategories">
                            <ng-container *ngTemplateOutlet="categoryGroup; context:{group: {label: 'Expense Categories', data$: $expenseCategories, legacyData$: $legacyDisabledAssets, category: 'expense'}}"></ng-container>
                        </ng-container>
                    </ng-container>

                    <ng-container *ngSwitchCase="'assets'">
                        <ng-container *ngIf="showAssets">
                            <ng-container *ngTemplateOutlet="categoryGroup; context:{group: {label: 'Assets', data$: $fixedAssets, legacyData$: $legacyDisabledAssets}}"></ng-container>
                        </ng-container>
                    </ng-container>

                    <ng-container *ngSwitchCase="'liabilities'">
                        <ng-container *ngIf="showLiabilities">
                            <ng-container *ngTemplateOutlet="categoryGroup; context:{group: {label: 'Liabilities', data$: $liabilities, legacyData$: $legacyDisabledLiabilities}}"></ng-container>
                        </ng-container>
                    </ng-container>

                    <ng-container *ngSwitchCase="'equities'">
                        <ng-container *ngIf="showEquities">
                            <ng-container *ngTemplateOutlet="categoryGroup; context:{group: {label: 'Equities', data$: $equities, legacyData$: $legacyDisabledEquities}}"></ng-container>
                        </ng-container>
                    </ng-container>
                </ng-container>
            </ng-container>

            <ng-template #categoryGroup let-group="group">
                <ng-container *ngIf="(group.data$ | async) as data">
                    <ng-container *ngIf="(group.legacyData$ | async) as legacyData">
                        <mat-optgroup *ngIf="data.length || legacyData.length || (showAddCategory && group.category)">
                            {{ group.label }}
                            <mat-option *ngIf="showAddCategory && group.category"
                                        [value]="-1"
                                        (click)="addCategory(group.category)">
                                <mat-icon class="scale-small ml-n2 mr-0">add</mat-icon>
                                {{ 'Add New Category' }}
                            </mat-option>

                            <ng-container *ngFor="let option of data">
                                <mat-option [value]="option">
                                    <div class="d-flex justify-content-between align-items-center" [class.text-danger]="option.deletedAt" [matTooltip]="'Deleted ' + (option.deletedAt | legfiDate:'MM/DD/YYYY')" [matTooltipDisabled]="!option.deletedAt">
                                        {{ option.label }} <span *ngIf="option.deletedAt" class="text-muted">(deleted)</span>
                                        <div>
                                            <app-button *ngIf="option.children?.length > 0 && multiple"
                                                        type="link"
                                                        [buttonClasses]="['mr-0']"
                                                        [matTooltip]="'Toggle child options for ' + option.label + ''"
                                                        (onClick)="$event.stopPropagation(); toggleOptionChildren(option)">
                                                {{ 'All Children' }}
                                            </app-button>
                                        </div>
                                    </div>
                                </mat-option>
                                <ng-container *ngIf="option.children">
                                    <mat-option *ngFor="let child of (option | as: Category).children"
                                            class="mat-child-option category-child-indent"
                                                [value]="child">
                                        <div class="d-flex justify-content-between align-items-center" [class.text-danger]="child.deletedAt" [matTooltip]="'Deleted ' + (child.deletedAt | legfiDate:'MM/DD/YYYY')" [matTooltipDisabled]="!child.deletedAt">
                                            {{ child.label }} <span *ngIf="child.deletedAt" class="text-muted">(deleted)</span>
                                        </div>
                                </mat-option>
                                </ng-container>
                            </ng-container>
                            
                            <!-- Legacy disabled accounts (shown as disabled, typically non-asset accounts from old transfers) -->
                            <ng-container *ngFor="let legacyOption of legacyData">
                                <mat-option [value]="legacyOption" [disabled]="true">
                                    <div class="d-flex justify-content-between align-items-center text-muted">
                                        {{ legacyOption.label }} <span class="text-muted">(legacy - cannot be selected)</span>
                                    </div>
                                </mat-option>
                            </ng-container>
                        </mat-optgroup>
                    </ng-container>
                </ng-container>
            </ng-template>
        </mat-select>
        <mat-spinner *ngIf="isLoading" [diameter]="15"></mat-spinner>
    </mat-form-field>
    <button *ngIf="multiple" class="btn btn-link m-0 p-0 text-sm" (click)="$event.stopPropagation(); resetSelections()">
        Clear Selections
    </button>
</ng-container>


